import { Badge, Card, EmptyState, Switch } from '@aurora/ui-kit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../api/client.js';
import { useModules } from '../modules/useModules.js';
import { useFeatures } from '../state/useFeatures.js';

/** Verwaltungsseite: Module und Funktionen für den aktiven Mandanten ein-/ausschalten. */
export function ModulesPage() {
  const modules = useModules();
  const { features } = useFeatures();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>(undefined);

  const toggle = useMutation({
    mutationFn: (input: { name: string; enabled: boolean }) =>
      api<{ ok: boolean }>(`/api/v1/modules/${input.name}`, {
        method: 'PUT',
        body: { enabled: input.enabled },
      }),
    onSuccess: () => {
      setError(undefined);
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
    onError: (mutationError) => {
      setError(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung, Module zu verwalten (core.modules.manage).'
          : 'Änderung fehlgeschlagen — bitte erneut versuchen.',
      );
    },
  });

  const toggleFeature = useMutation({
    mutationFn: (input: { key: string; enabled: boolean }) =>
      api(`/api/v1/features/${input.key}`, {
        method: 'PUT',
        body: { enabled: input.enabled },
      }),
    onSuccess: () => {
      setError(undefined);
      // Navigation/Widgets folgen sofort — alle Konsumenten hängen an ['features']
      void queryClient.invalidateQueries({ queryKey: ['features'] });
    },
    onError: (mutationError) => {
      setError(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung, Funktionen zu verwalten (core.modules.manage).'
          : 'Änderung fehlgeschlagen — bitte erneut versuchen.',
      );
    },
  });

  const states = modules.data?.states ?? [];

  return (
    <div className="page">
      <h1 className="page__title">Module</h1>
      <p className="page__lead">
        Aktiviere Module für diesen Mandanten. Global deaktivierte Module kann nur der Betreiber der
        Instanz freischalten.
      </p>
      {error !== undefined ? <p className="page__error">{error}</p> : null}

      <Card>
        {states.length === 0 ? (
          <EmptyState
            title="Keine Module installiert"
            description="Fachmodule (CRM, Buchhaltung, …) folgen ab Phase 3."
          />
        ) : (
          <ul className="module-list">
            {states.map((state) => (
              <li key={state.name} className="module-list__row">
                <div>
                  <div className="module-list__name">
                    {state.title ?? state.name}{' '}
                    <span className="module-list__version">v{state.version}</span>
                  </div>
                  <div className="module-list__meta">
                    {state.enabled ? (
                      <Badge tone="accent">global aktiv</Badge>
                    ) : (
                      <Badge tone="warning">global deaktiviert</Badge>
                    )}
                    {!state.registered ? <Badge tone="danger">Code fehlt</Badge> : null}
                  </div>
                </div>
                <Switch
                  checked={state.enabledForTenant}
                  disabled={!state.enabled || toggle.isPending}
                  aria-label={`${state.title ?? state.name} für diesen Mandanten umschalten`}
                  onChange={(enabled) => toggle.mutate({ name: state.name, enabled })}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <h2 className="page__title" style={{ fontSize: 'var(--au-text-xl)' }}>
        Funktionen
      </h2>
      <p className="page__lead">
        Kern-Funktionen der Plattform, unabhängig von Modulen. Abgeschaltete Funktionen verschwinden
        aus Navigation und Oberfläche; ihre Daten bleiben erhalten.
      </p>
      <Card>
        <ul className="module-list">
          {features.map((feature) => (
            <li key={feature.key} className="module-list__row">
              <div>
                <div className="module-list__name">{feature.label}</div>
                <div className="module-list__meta">{feature.description}</div>
              </div>
              <Switch
                checked={feature.enabled}
                disabled={toggleFeature.isPending}
                aria-label={`${feature.label} für diesen Mandanten umschalten`}
                onChange={(enabled) => toggleFeature.mutate({ key: feature.key, enabled })}
              />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
